import * as React from "react";
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Checkbox,
    CircularProgress,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import ManageHistoryIcon from "@mui/icons-material/ManageHistory";
import {useTranslation} from "react-i18next";
import WorkEvidenceTableComponentMobile from "./mobile/WorkEvidenceTableComponentMobile";
import StatusChip from "./StatusChip";
import {getAvatarColors, getInitials} from "../../utils/avatarUtils";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WorkEvidenceRecord {
    workEvidenceId: string | null;
    employeeId: string;
    firstName: string;
    lastName: string;
    regularHours: number | null;
    overTimeHours: number | null;
    nightHours: number | null;
    weekendHours: number | null;
    holidayHours: number | null;
    status: string | null;
    comment: string | null;
    /** Backend-computed regular hours prorated to the contract's start/end dates. */
    suggestedRegularHours?: number | null;
}

export type Order = "asc" | "desc";
export type SortableKey =
    | "firstName"
    | "lastName"
    | "regularHours"
    | "overTimeHours"
    | "nightHours"
    | "weekendHours"
    | "holidayHours";

export const editableColumns = [
    "regularHours",
    "overTimeHours",
    "nightHours",
    "weekendHours",
    "holidayHours",
] as const;

export type EditableColumn = (typeof editableColumns)[number];

const HOURS_PER_DAY = 8;

/** Days-worked, derived from regular hours (8 regular hours = 1 day) — not a stored field. */
export function computeDaysWorked(regularHours: number | null | undefined): string {
    if (regularHours === null || regularHours === undefined) return "-";
    return (regularHours / HOURS_PER_DAY).toFixed(1);
}

/** Resolves an editable column's value to use for display/validation, preferring an in-progress edit. */
export function getEffectiveValue(
    record: WorkEvidenceRecord,
    editedCells: { [key: string]: string },
    column: EditableColumn,
): number | null {
    const edited = editedCells[`${record.employeeId}-${column}`];
    if (edited === undefined || edited === "") return record[column];
    const numeric = Number.parseFloat(edited);
    return Number.isNaN(numeric) ? record[column] : numeric;
}

/** Resolves the regular-hours value to use for display, preferring an in-progress edit. */
export function getEffectiveRegularHours(
    record: WorkEvidenceRecord,
    editedCells: { [key: string]: string },
): number | null {
    return getEffectiveValue(record, editedCells, "regularHours");
}

export interface RecordValidation {
    hoursFilled: boolean;
    noNegativeValues: boolean;
}

/** Client-side data checks used by the "data check" panel on desktop and mobile. */
export function validateRecord(
    record: WorkEvidenceRecord,
    editedCells: { [key: string]: string },
): RecordValidation {
    const values = editableColumns.map((column) => getEffectiveValue(record, editedCells, column));
    return {
        hoursFilled: (values[0] ?? 0) > 0,
        noNegativeValues: values.every((value) => value === null || value >= 0),
    };
}

// ─── Sorting helpers ──────────────────────────────────────────────────────────

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
    const aVal = a[orderBy] ?? "";
    const bVal = b[orderBy] ?? "";
    if (bVal < aVal) return -1;
    if (bVal > aVal) return 1;
    return 0;
}

export function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

// ─── EditableCell sub-component ───────────────────────────────────────────────

interface EditableCellProps {
    cellId: string;
    currentValue: number | null;
    editedValue: string | undefined;
    isEditable: boolean;
    isActive: boolean;
    onActivate: (id: string) => void;
    onBlur: () => void;
    onChange: (id: string, value: string) => void;
}

function EditableCell({
                          cellId,
                          currentValue,
                          editedValue,
                          isEditable,
                          isActive,
                          onActivate,
                          onBlur,
                          onChange,
                      }: EditableCellProps): React.ReactElement {
    const displayValue = editedValue ?? currentValue ?? "-";

    if (isEditable && isActive) {
        return (
            <TableCell
                sx={{
                    padding: 0,
                    position: "relative",
                    height: "44px",
                }}
            >
                <TextField
                    size="small"
                    value={editedValue ?? currentValue ?? ""}
                    variant="outlined"
                    onChange={(e) => onChange(cellId, e.target.value)}
                    onBlur={onBlur}
                    autoFocus
                    fullWidth
                    type="number"
                    inputProps={{step: "0.01"}}
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        "& .MuiOutlinedInput-root": {height: "100%", borderRadius: 0},
                        "& .MuiOutlinedInput-input": {
                            padding: "8px 14px",
                            height: "100%",
                            boxSizing: "border-box",
                        },
                    }}
                />
            </TableCell>
        );
    }

    return (
        <TableCell sx={{cursor: isEditable ? "pointer" : "default"}}>
            <Typography
                variant="body2"
                onClick={() => isEditable && onActivate(cellId)}
                sx={{
                    cursor: isEditable ? "pointer" : "default",
                    "&:hover": isEditable ? {textDecoration: "underline"} : {},
                }}
            >
                {displayValue}
            </Typography>
        </TableCell>
    );
}

// ─── Empty-state placeholder ──────────────────────────────────────────────────

function EmptyPlaceholder({detail}: { detail?: string }): React.ReactElement {
    const {t} = useTranslation();
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 300,
                gap: 1,
            }}
        >
            <ManageHistoryIcon sx={{fontSize: 48, color: "text.disabled"}}/>
            <Typography variant="body1" color="text.secondary">
                {t('workEvidence.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{textAlign: "center"}}>
                {detail ?? t('workEvidence.empty.description')}
            </Typography>
        </Box>
    );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface WorkEvidenceTableComponentProps {
    selectedPeriod: string | null;
    selectedBranch: string | null;
    isLoadingData: boolean;
    tableDataError: string | null;
    workEvidenceData: WorkEvidenceRecord[];
    filteredData: WorkEvidenceRecord[];
    currentPage: number;
    itemsPerPage: number;
    isPeriodOpen: boolean;
    editedCells: { [key: string]: string };
    activeCellId: string | null;
    order: Order;
    orderBy: SortableKey;
    handleRequestSort: (property: SortableKey) => void;
    handleCellActivate: (cellId: string) => void;
    handleCellBlur: () => void;
    handleCellValueChange: (cellId: string, value: string) => void;
    selected: string[];
    isSelected: (id: string) => boolean;
    onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRowCheckboxClick: (event: React.MouseEvent, id: string) => void;
    onPageChange: (page: number) => void;
    commentDrafts: { [employeeId: string]: string };
    onCommentChange: (employeeId: string, value: string) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function WorkEvidenceTableComponent({
                                                       selectedPeriod,
                                                       selectedBranch,
                                                       isLoadingData,
                                                       tableDataError,
                                                       workEvidenceData,
                                                       filteredData,
                                                       currentPage,
                                                       itemsPerPage,
                                                       isPeriodOpen,
                                                       editedCells,
                                                       activeCellId,
                                                       order,
                                                       orderBy,
                                                       handleRequestSort,
                                                       handleCellActivate,
                                                       handleCellBlur,
                                                       handleCellValueChange,
                                                       selected,
                                                       isSelected,
                                                       onSelectAllClick,
                                                       onRowCheckboxClick,
                                                       onPageChange,
                                                       commentDrafts,
                                                       onCommentChange,
                                                   }: WorkEvidenceTableComponentProps): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    if (!selectedPeriod || !selectedBranch) {
        return <EmptyPlaceholder/>;
    }

    if (isLoadingData) {
        return isMobile ? (
            <WorkEvidenceTableComponentMobile
                sortableData={[]}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                isPeriodOpen={isPeriodOpen}
                editedCells={editedCells}
                activeCellId={activeCellId}
                handleCellActivate={handleCellActivate}
                handleCellBlur={handleCellBlur}
                handleCellValueChange={handleCellValueChange}
                selected={selected}
                isSelected={isSelected}
                onRowCheckboxClick={onRowCheckboxClick}
                commentDrafts={commentDrafts}
                onCommentChange={onCommentChange}
                isLoading
            />
        ) : (
            <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (tableDataError) {
        return (
            <Alert severity="error" sx={{mt: 2}}>
                <AlertTitle>{t('common.error.title')}</AlertTitle>
                {tableDataError}
            </Alert>
        );
    }

    if (!workEvidenceData || workEvidenceData.length === 0) {
        return <EmptyPlaceholder/>;
    }

    if (filteredData.length === 0) {
        return <EmptyPlaceholder detail={t('workEvidence.empty.noMatches')}/>;
    }

    const pageRows = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selected.includes(r.employeeId));
    const somePageSelected = pageRows.some((r) => selected.includes(r.employeeId)) && !allPageSelected;

    if (isMobile) {
        return (
            <>
                <WorkEvidenceTableComponentMobile
                    sortableData={filteredData}
                    currentPage={currentPage}
                    itemsPerPage={itemsPerPage}
                    isPeriodOpen={isPeriodOpen}
                    editedCells={editedCells}
                    activeCellId={activeCellId}
                    handleCellActivate={handleCellActivate}
                    handleCellBlur={handleCellBlur}
                    handleCellValueChange={handleCellValueChange}
                    selected={selected}
                    isSelected={isSelected}
                    onRowCheckboxClick={onRowCheckboxClick}
                    commentDrafts={commentDrafts}
                    onCommentChange={onCommentChange}
                />
                <Box sx={{display: "flex", justifyContent: "center", mt: 2}}>
                    <Pagination
                        count={Math.ceil(filteredData.length / itemsPerPage)}
                        color="primary"
                        page={currentPage}
                        onChange={(_event, page) => onPageChange(page)}
                    />
                </Box>
            </>
        );
    }

    return (
        <>
            <TableContainer component={Paper} variant="outlined" sx={{mt: 2, overflowX: "auto"}}>
                <Table sx={{minWidth: {xs: 700, md: 1000}}}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    color="primary"
                                    indeterminate={somePageSelected}
                                    checked={allPageSelected}
                                    onChange={onSelectAllClick}
                                    inputProps={{"aria-label": t('workEvidence.aria.selectAllRecords')}}
                                />
                            </TableCell>
                            <TableCell sortDirection={orderBy === "firstName" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "firstName"}
                                    direction={orderBy === "firstName" ? order : "asc"}
                                    onClick={() => handleRequestSort("firstName")}
                                >
                                    {t('workEvidence.table.header.employee')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === "regularHours" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "regularHours"}
                                    direction={orderBy === "regularHours" ? order : "asc"}
                                    onClick={() => handleRequestSort("regularHours")}
                                >
                                    {t('workEvidence.table.header.regularHours')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === "overTimeHours" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "overTimeHours"}
                                    direction={orderBy === "overTimeHours" ? order : "asc"}
                                    onClick={() => handleRequestSort("overTimeHours")}
                                >
                                    {t('workEvidence.table.header.overtimeHours')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === "nightHours" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "nightHours"}
                                    direction={orderBy === "nightHours" ? order : "asc"}
                                    onClick={() => handleRequestSort("nightHours")}
                                >
                                    {t('workEvidence.table.header.nightHours')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === "weekendHours" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "weekendHours"}
                                    direction={orderBy === "weekendHours" ? order : "asc"}
                                    onClick={() => handleRequestSort("weekendHours")}
                                >
                                    {t('workEvidence.table.header.weekendHours')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={orderBy === "holidayHours" ? order : false}>
                                <TableSortLabel
                                    active={orderBy === "holidayHours"}
                                    direction={orderBy === "holidayHours" ? order : "asc"}
                                    onClick={() => handleRequestSort("holidayHours")}
                                >
                                    {t('workEvidence.table.header.holidayHours')}
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>{t('workEvidence.table.header.daysWorked')}</TableCell>
                            <TableCell>{t('hr.branches.table.header.status')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageRows.map((record) => {
                            const rowSelected = isSelected(record.employeeId);
                            const avatarColors = getAvatarColors(`${record.firstName}${record.lastName}${record.employeeId}`);
                            return (
                                <TableRow key={record.employeeId} hover selected={rowSelected}>
                                    <TableCell padding="checkbox"
                                               onClick={(e) => onRowCheckboxClick(e, record.employeeId)}>
                                        <Checkbox
                                            color="primary"
                                            checked={rowSelected}
                                            inputProps={{"aria-label": t('workEvidence.aria.selectRecord', {firstName: record.firstName, lastName: record.lastName})}}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                                            <Avatar sx={{
                                                width: 36,
                                                height: 36,
                                                bgcolor: avatarColors.bg,
                                                color: avatarColors.color,
                                                fontSize: "0.875rem",
                                                fontWeight: 600,
                                            }}>
                                                {getInitials(record.firstName, record.lastName)}
                                            </Avatar>
                                            <Typography variant="body2" fontWeight={600}>
                                                {record.firstName} {record.lastName}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    {editableColumns.map((column) => (
                                        <EditableCell
                                            key={column}
                                            cellId={`${record.employeeId}-${column}`}
                                            currentValue={record[column]}
                                            editedValue={editedCells[`${record.employeeId}-${column}`]}
                                            isEditable={isPeriodOpen}
                                            isActive={activeCellId === `${record.employeeId}-${column}`}
                                            onActivate={handleCellActivate}
                                            onBlur={handleCellBlur}
                                            onChange={handleCellValueChange}
                                        />
                                    ))}
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {computeDaysWorked(getEffectiveRegularHours(record, editedCells))}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <StatusChip status={record.status}/>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{display: "flex", justifyContent: "center", mt: 2}}>
                <Pagination
                    count={Math.ceil(filteredData.length / itemsPerPage)}
                    color="primary"
                    page={currentPage}
                    onChange={(_event, page) => onPageChange(page)}
                />
            </Box>
        </>
    );
}
